import { Navigate, useNavigate } from 'react-router-dom'
import { lazy, Suspense, type ComponentType } from 'react'
import { Helmet } from 'react-helmet'
import { APP } from '@/common/strings'
import { APP_ROUTES } from '@/common/constants'
import { ONBOARDING_COPY } from '@/locales/en'
import { useLocalFlags } from '@/common/featureFlags/local/useLocalFlags'
import { FullScreenLoader } from '@/common/ui/FullScreenLoader'
import { usePreloadApplicationContainer } from '@/application/usePreloadApplicationContainer'
import type { OnboardingCompleteV2Props } from './features/onboarding-complete-v2/components/OnboardingCompleteV2'

let LazyOnboardingCompleteV2: ComponentType<OnboardingCompleteV2Props> | undefined

if (import.meta.env.DEV || import.meta.env.VITE_ENV === 'dev') {
    LazyOnboardingCompleteV2 = lazy(() =>
        import('./features/onboarding-complete-v2/components/OnboardingCompleteV2').then((m) => ({
            default: m.OnboardingCompleteV2,
        })),
    )
}

/** Production page for the /onboarding/signed-up route — only reachable from the v2 onboarding
 *  flow (see MobileVerifyPage's post-verification navigate). Falls back to the dashboard if
 *  landed on directly while v2 is off, since there is no v1 equivalent of this screen. */
export function SignedUpPage() {
    const navigate = useNavigate()
    const { showOnboardingV2, showApplicationV2 } = useLocalFlags()

    usePreloadApplicationContainer(
        Boolean(showOnboardingV2) && Boolean(showApplicationV2) && !!LazyOnboardingCompleteV2,
    )

    if (!(showOnboardingV2 && LazyOnboardingCompleteV2)) {
        return <Navigate to={APP_ROUTES.DASHBOARD} replace />
    }

    const OnboardingCompleteV2 = LazyOnboardingCompleteV2

    return (
        <>
            <Helmet>
                <title>
                    {ONBOARDING_COPY.onboardingCompleteV2.title} | {APP.title}
                </title>
            </Helmet>
            <Suspense fallback={<FullScreenLoader dark />}>
                <OnboardingCompleteV2
                    onOpenTradingAccount={() => navigate(APP_ROUTES.APP_ACCOUNT_OPEN)}
                    onExploreClick={() => navigate(APP_ROUTES.DASHBOARD)}
                />
            </Suspense>
        </>
    )
}
